import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckSquare, ArrowRight, Expand } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { productImg } from "@/lib/img";
import PAGES from "@/data/productPages.json";

export const pageFor = (key) => PAGES.products[key] || { for: "", pairs: [], faq: [] };
export const productHref = (p) => p.page || `/shop/${p.lookup_key}`;
const KYTO = "Written by Coach Kyto, CSPC: varsity coach, youth sports parent and 15+ years playing semi-pro football.";

const Section = ({ title, testId, children }) => (
  <section className="mt-14" data-testid={testId}>
    <h2 className="font-head font-semibold uppercase text-2xl sm:text-3xl text-[#1E1E1E] leading-tight">{title}</h2>
    <div className="mt-5">{children}</div>
  </section>
);

export function WhoFor({ k, text: override }) {
  const text = override || pageFor(k).for;
  if (!text) return null;
  return (
    <Section title="Who it's for" testId="product-who-for">
      <p className="text-lg text-[#3A3A3A] border-l-[3px] border-[#E8A33D] pl-4 max-w-2xl" data-testid="product-who-for-text">{text}</p>
    </Section>
  );
}

export function InsideList({ items, before }) {
  if (!items.length && !before) return null;
  return (
    <Section title="What's inside" testId="product-inside">
      {before}
      <ul className="grid sm:grid-cols-2 gap-3" data-testid="product-inside-list">
        {items.map((it, i) => (
          <li key={it.label} className="flex gap-3 items-start bg-white border-2 border-[#D3CFC0] px-4 py-3 text-sm text-[#1E1E1E]" data-testid={`product-inside-item-${i}`}>
            <CheckSquare size={18} className="text-[#DB9130] shrink-0 mt-0.5" />
            {it.to ? <Link to={it.to} className="underline underline-offset-4 hover:text-[#DB9130]">{it.label}</Link> : <span>{it.label}</span>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

export const previewsFor = (p) => {
  const k = p.lookup_key;
  if (p.is_bundle) return (p.bundle_of || []).map((b, i) => ({ src: `/products/preview-${b}-1.png`, alt: `Inside ${p.includes?.[i] || b}` }));
  return [1, 2].map((n) => ({ src: `/products/preview-${k}-${n}.png`, alt: `${p.name}: sample page ${n}` }));
};

export function PreviewGallery({ images }) {
  const [open, setOpen] = useState(null);
  if (!images.length) return null;
  const cols = images.length > 2 ? "lg:grid-cols-3" : "lg:grid-cols-2";
  return (
    <Section title="Take a look inside" testId="product-previews">
      <div className={`flex lg:grid ${cols} gap-4 overflow-x-auto snap-x snap-mandatory scroll-px-4 lg:scroll-px-0 -mx-4 px-4 lg:mx-0 lg:px-0 pb-2`}>
        {images.map((im, i) => (
          <button key={im.src} type="button" onClick={() => setOpen(im)} data-testid={`product-preview-${i}`}
            className="group relative shrink-0 w-[78%] sm:w-[46%] lg:w-auto snap-start border-2 border-[#D3CFC0] bg-white hover:border-[#1E1E1E] transition-colors">
            <img {...productImg(im.src, "(min-width: 1024px) 33vw, 80vw")} alt={im.alt} loading="lazy" decoding="async" className="w-full h-auto block" />
            <span className="absolute bottom-2 right-2 bg-[#1E1E1E] text-[#F3F1EC] p-1.5 opacity-80 group-hover:opacity-100"><Expand size={14} /></span>
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl p-2 bg-[#F3F1EC]" data-testid="product-preview-lightbox">
          <DialogTitle className="sr-only">{open?.alt}</DialogTitle>
          {open && <img src={open.src} alt={open.alt} className="w-full h-auto max-h-[85vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

export const KytoNote = () => (
  <Section title="From Coach Kyto" testId="product-kyto">
    <div className="flex items-center gap-4 max-w-2xl">
      <img src="/coach-kyto-sideline-480.webp" alt="Coach Kyto" loading="lazy" className="w-16 h-16 object-cover border-2 border-[#1E1E1E]" style={{ filter: "grayscale(1)" }} />
      <p className="text-[#3A3A3A]" data-testid="product-kyto-text">{KYTO}</p>
    </div>
  </Section>
);

const REFUND = "voluntoldhq.com/refund";
const Answer = ({ text }) => {
  if (!text.includes(REFUND)) return text;
  const [a, b] = text.split(REFUND);
  return <>{a}<Link to="/refund" className="underline underline-offset-4 text-[#1E1E1E]" data-testid="faq-refund-link">{REFUND}</Link>{b}</>;
};

export function ProductFaq({ k, extra, refundQuestion }) {
  const shared = refundQuestion ? PAGES.sharedFaq.filter(([q]) => q.startsWith("What if")).map(([, a]) => [refundQuestion, a]) : PAGES.sharedFaq;
  const items = [...(extra || pageFor(k).faq), ...shared];
  return (
    <Section title="FAQ" testId="product-faq">
      <Accordion type="single" collapsible className="max-w-3xl border-t-2 border-[#D3CFC0]">
        {items.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="border-b-2 border-[#D3CFC0]">
            <AccordionTrigger className="text-left font-head uppercase tracking-wide text-sm sm:text-base text-[#1E1E1E] hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-[#3A3A3A] text-base leading-relaxed" data-testid={`faq-a-${i}`}><Answer text={a} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

export function PairsWell({ k, products, keys }) {
  const list = (keys || pageFor(k).pairs).map((key) => products.find((p) => p.lookup_key === key)).filter(Boolean);
  if (!list.length) return null;
  return (
    <Section title="Pairs well with" testId="product-pairs">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {list.map((p) => (
          <Link key={p.lookup_key} to={productHref(p)} data-testid={`pair-card-${p.lookup_key}`}
            className="group flex flex-col bg-[#E7E3D9] border-2 border-[#D3CFC0] hover:border-[#2D2D2D] transition-colors">
            <div className="aspect-[4/3] bg-[#F3F1EC] border-b-2 border-[#D3CFC0]">
              <img {...productImg(p.image, "(min-width: 1024px) 33vw, 50vw")} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-contain p-3" />
            </div>
            <div className="p-4 flex flex-col flex-1">
              <span className="font-head font-semibold text-base sm:text-lg text-[#1E1E1E] leading-tight group-hover:underline underline-offset-4 flex-1">{p.name}</span>
              <span className="mt-3 flex items-center justify-between font-head text-xl text-[#1E1E1E]">${p.amount / 100} <ArrowRight size={16} className="text-[#DB9130]" /></span>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

export function ProductDetails({ p, products, insideBefore }) {
  const k = p.lookup_key;
  const inside = p.is_bundle
    ? (p.includes || []).map((label, i) => ({ label, to: p.bundle_of?.[i] ? `/shop/${p.bundle_of[i]}` : undefined }))
    : (p.contents?.length ? p.contents : p.includes || []).map((label) => ({ label }));
  return (
    <>
      <WhoFor k={k} text={p.who_for} />
      <InsideList items={p.line && p.is_bundle ? [] : inside} before={insideBefore || (p.line && <LineInsideTop p={p} products={products} />)} />
      {!p.line && <PreviewGallery images={previewsFor(p)} />}
      {!p.line && <KytoNote />}
      <ProductFaq k={k} />
      <PairsWell k={k} products={products} keys={p.pairs?.length ? p.pairs : p.line ? [] : undefined} />
    </>
  );
}

function LineInsideTop({ p, products }) {
  if (!p.is_bundle) return p.pages ? <p className="mb-4 text-sm text-[#3A3A3A]" data-testid="product-pages">{p.pages} pages</p> : null;
  const books = (p.bundle_of || []).map((key) => products.find((x) => x.lookup_key === key)).filter(Boolean);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="bundle-books">
      {books.map((b) => (
        <Link key={b.lookup_key} to={`/shop/${b.lookup_key}`} data-testid={`bundle-book-${b.lookup_key}`}
          className="group block border-2 border-[#D3CFC0] bg-white hover:border-[#1E1E1E] transition-colors">
          <img {...productImg(b.image, "(min-width: 640px) 25vw, 50vw")} alt={b.name} loading="lazy" className="w-full aspect-[3/2] object-contain bg-[#F3F1EC] p-2" />
          <span className="block p-3 text-sm font-bold text-[#1E1E1E] leading-tight group-hover:underline">{b.name}</span>
          <span className="block px-3 pb-3 text-xs text-[#6E6E6E]">{b.free ? "Free" : `$${b.amount / 100}`}{b.pages ? ` · ${b.pages} pages` : ""}</span>
        </Link>
      ))}
    </div>
  );
}
